import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice ROI - Calculez le retour sur investissement";
const DESCRIPTION =
  "Utilisez la calculatrice ROI gratuite pour calculer le rendement de vos investissements. Calculez le benefice total, le pourcentage de retour et le ROI annualise.";
const KEYWORDS =
  "calculatrice ROI, retour sur investissement, calcul rentabilite projet, ROI annualise, ROI marketing, ROI investissement";
const OG_URL = "https://do-calculate.com/calculator/fr/roi-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice ROI",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "FinancialApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
  },
];

function RoiCalculator() {
  const [amountInvested, setAmountInvested] = useState("");
  const [amountReturned, setAmountReturned] = useState("");
  const [investmentPeriod, setInvestmentPeriod] = useState("");
  const [periodUnit, setPeriodUnit] = useState("years");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateROI = () => {
    const invested = parseFloat(amountInvested);
    const returned = parseFloat(amountReturned);
    const period = parseFloat(investmentPeriod);

    if (isNaN(invested) || isNaN(returned) || invested <= 0) {
      setResult(null); return;
    }

    const totalProfit = returned - invested;
    const roiPercentage = (totalProfit / invested) * 100;

    let annualizedROI = null;
    if (!isNaN(period) && period > 0) {
      const years = periodUnit === "years" ? period : period / 12;
      annualizedROI = (Math.pow(returned / invested, 1 / years) - 1) * 100;
    }

    setResult({
      totalProfit,
      roiPercentage,
      annualizedROI,
      investmentGain: (returned / invested).toFixed(2),
    });
  };

  useEffect(() => { calculateROI(); }, [amountInvested, amountReturned, investmentPeriod, periodUnit]);

  const handleClear = () => {
    setAmountInvested(""); setAmountReturned(""); setInvestmentPeriod("");
    setPeriodUnit("years"); setResult(null); setError("");
  };

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} />
      <div className="row"><div className="col-12 col-md-8"><FirstAds /></div></div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice ROI - Retour sur investissement</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>La calculatrice ROI (Retour sur investissement)</strong> est un outil financier utilise pour
                evaluer l&apos;efficacite d&apos;un investissement ou comparer plusieurs investissements. Elle mesure le
                rendement d&apos;un investissement par rapport a son cout.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Donnees de l&apos;investissement</h2>
              </div>
              <div className="row mb-3">
                <div className="col-sm-12 col-md-6">
                  <label>Montant investi</label>
                  <input type="number" className="form-control" value={amountInvested}
                    onChange={(e) => setAmountInvested(e.target.value)} placeholder="Ex: 10000" />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Montant recupere (valeur finale)</label>
                  <input type="number" className="form-control" value={amountReturned}
                    onChange={(e) => setAmountReturned(e.target.value)} placeholder="Ex: 12000" />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-12 col-md-6">
                  <label>Duree de l&apos;investissement (optionnel)</label>
                  <input type="number" className="form-control" value={investmentPeriod}
                    onChange={(e) => setInvestmentPeriod(e.target.value)} placeholder="Duree" />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Unite de duree</label>
                  <select className="form-select" value={periodUnit} onChange={(e) => setPeriodUnit(e.target.value)}>
                    <option value="years">Annees</option>
                    <option value="months">Mois</option>
                  </select>
                </div>
              </div>
              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  Effacer les donnees
                </button>
              </div>
              {result && (
                <div className="mt-4">
                  <h3 className="text-center mb-3">Resultats</h3>
                  <table className="table bmi-table">
                    <thead>
                      <tr>
                        <th>Indicateur</th>
                        <th>Valeur</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Benefice / Perte total(e)</td>
                        <td style={{ fontWeight: "bold", color: result.totalProfit >= 0 ? "#1a5f3f" : "#d9534f" }}>
                          {result.totalProfit.toLocaleString()}
                        </td>
                      </tr>
                      <tr>
                        <td>Taux de retour sur investissement (ROI)</td>
                        <td style={{ fontWeight: "bold" }}>{result.roiPercentage.toFixed(2)}%</td>
                      </tr>
                      {result.annualizedROI !== null && (
                        <tr>
                          <td>ROI annualise</td>
                          <td style={{ fontWeight: "bold", color: "#007bff" }}>
                            {result.annualizedROI.toFixed(2)}%
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>Multiplicateur de l&apos;investissement</td>
                        <td>{result.investmentGain}x</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="info-sections-container">
              <div className="info-section">
                <div className="info-section-header">Comment calculer le ROI</div>
                <div className="info-section-body">
                  <p>La formule de base du ROI consiste a soustraire le cout initial de l&apos;investissement de sa valeur finale, puis a diviser le resultat par le cout initial :</p>
                  <div className="alert alert-info text-center" style={{ direction: "ltr" }}>
                    ROI = ((Valeur finale - Investissement initial) / Investissement initial) x 100
                  </div>
                  <p>Ce ratio aide les investisseurs a connaitre le succes d&apos;un investissement par rapport a d&apos;autres opportunites.</p>
                </div>
              </div>
              <div className="info-section">
                <div className="info-section-header">Qu&apos;est-ce que le ROI annualise ?</div>
                <div className="info-section-body">
                  <p>Alors que le ROI simple indique le benefice total sur toute la duree, le ROI annualise montre le rendement moyen par annee. Cela est essentiel pour comparer des investissements de durees differentes.</p>
                  <ul>
                    <li>Un investissement sur 5 ans a 50 % de ROI ne signifie pas 10 % par an — le calcul utilise les interets composes.</li>
                    <li>Le ROI annualise aide a prendre des decisions financieres plus intelligentes entre actions, immobilier ou obligations.</li>
                  </ul>
                </div>
              </div>
            </div>
            <hr />
            <div className="row"><RelatedLinks /></div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds /><br />
          <MainCalculatorsList />
        </div>
      </div>
    </div>
  );
}

export default RoiCalculator;
